import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/sections";
import Lightbox from "@/components/Lightbox";
import { ArrowUpRight } from "lucide-react";

const HERO = "https://images.pexels.com/photos/18486408/pexels-photo-18486408.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940";

const POSTER = "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/y1ebs4dl_Pottery%20session%20poster%202026.jpg";

const FALLBACK_IN_PROGRESS = [
  { url: POSTER, caption: "Programme · Clay With Us" },
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/za9k0zzv_IMG20260718164259.webp", caption: "Wheel session · afternoon" },
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/jdszebu5_IMG20260613160400.webp", caption: "Studio · centering clay" },
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/9karb8cx_IMG20260613160533.webp", caption: "Hand-built · around the table" },
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/wfegvs87_IMG20260718172722.webp", caption: "Wheel & hand-built · together" },
];

export default function CeramicStudio() {
  const [events, setEvents] = useState([]);
  const [lbIdx, setLbIdx] = useState(-1);
  const [photos, setPhotos] = useState(FALLBACK_IN_PROGRESS);
  useEffect(() => {
    api.get("/events", { params: { section: "ceramic-studio" } })
      .then(r => setEvents(r.data)).catch(() => {});
    api.get("/page-galleries/ceramic-studio")
      .then(r => { if (r.data?.photos?.length) setPhotos(r.data.photos); })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="ceramic-studio-page">
      <section className="relative h-[80vh] overflow-hidden">
        <img src={HERO} alt="Ceramic Studio" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0" style={{background: "linear-gradient(to bottom, rgba(0,0,0,0.0), rgba(0,0,0,0.45))"}}/>
        <div className="absolute bottom-0 left-0 p-6 md:p-12 lg:p-20 text-cream" style={{color:"#F9F8F6"}}>
          <div className="overline" style={{color:"#D4A373"}}>03 — Ceramic Studio</div>
          <h1 className="display text-6xl md:text-8xl mt-6">Clay <em className="italic">with us.</em></h1>
          <p className="mt-6 max-w-xl text-base" style={{color:"#E5E4DE"}}>
            A fully equipped studio: wheels, slab roller, kilns and a deep cabinet of glazes.
            Open to absolute beginners and serious practitioners alike.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-24 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="overline">The process</div>
          <h2 className="display text-4xl md:text-5xl mt-4">From earth, by hand.</h2>
          <p className="mt-6 text-base text-muted2 leading-relaxed">
            We believe ceramics is one of the most generous teachers — it asks for attention, patience and a willingness
            to begin again. Every course leaves you with your own fired pieces.
          </p>
          <blockquote className="mt-8 font-serif italic text-2xl leading-snug border-l-2 border-terra pl-5">
            &ldquo;The comfort zone is the great enemy to creativity.&rdquo;
          </blockquote>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <Link to="/events?section=ceramic-studio" className="btn-primary" data-testid="ceramic-book">Book a session</Link>
            <button
              type="button"
              onClick={() => setLbIdx(0)}
              className="inline-flex items-center gap-2 text-sm hover:text-terra transition group"
              data-testid="ceramic-classes-in-progress"
            >
              <span className="border-b border-charcoal group-hover:border-terra transition pb-0.5">Photos from recent classes</span>
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              <span className="overline" style={{fontSize:'0.55rem'}}>{photos.length} photos</span>
            </button>
          </div>
        </div>
        <div className="lg:col-span-7">
          <button
            type="button"
            onClick={() => setLbIdx(0)}
            className="block group text-left w-full"
            data-testid="ceramic-poster-open"
          >
            <div className="border border-soft overflow-hidden bg-white">
              <img
                src={POSTER}
                alt="Clay With Us — pottery courses poster"
                className="reveal-img w-full h-auto object-contain"
                loading="lazy"
              />
            </div>
            <div className="mt-3 overline flex items-center gap-2">Clay With Us · tap to view programme <span className="text-terra">↗</span></div>
          </button>
        </div>
      </section>

      <section className="bg-cream-2 py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="overline">Courses on offer</div>
          <h2 className="display text-4xl md:text-5xl mt-4">
            {events.length === 0 ? "Coming soon." : events.length === 1 ? "One way in." : `${events.length} ways in.`}
          </h2>
          {events.length === 0 && (
            <p className="text-sm text-muted2 mt-6 max-w-md">New pottery sessions will be listed here soon. Check back — or drop us a line and we'll notify you.</p>
          )}
          <div className="numbered mt-12 grid md:grid-cols-2 gap-10">
            {events.map((c) => (
              <Link
                to={`/events/${c.slug}`}
                key={c.id}
                className="flex items-start gap-5 group"
                data-testid={`ceramic-course-${c.slug}`}
              >
                <div className="grid grid-cols-[92px_1fr] gap-5 flex-1">
                  <div className="aspect-[4/5] overflow-hidden bg-cream border border-soft self-start">
                    {c.image_url ? (
                      <img src={c.image_url} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[9px] tracking-widest uppercase text-muted2">No image</div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-baseline gap-4 flex-wrap">
                      <div className="font-serif text-3xl tracking-tight group-hover:text-terra transition-colors">{c.title}</div>
                      {c.is_featured && (
                        <span className="px-1.5 py-0.5 text-[9px] tracking-widest uppercase" style={{background:"#D4A373",color:"#1C1C19"}} data-testid={`ceramic-highlight-${c.slug}`}>Highlight</span>
                      )}
                      {c.price_inr > 0 && (
                        <div className="font-serif text-xl text-terra">{formatINR(c.price_inr)}</div>
                      )}
                    </div>
                    {(c.recurrence_label || c.short_description) && (
                      <p className="text-sm text-muted2 mt-3 max-w-md leading-relaxed">
                        {c.recurrence_label && <span className="text-charcoal">{c.recurrence_label}. </span>}
                        {c.short_description}
                      </p>
                    )}
                    <div className="mt-3 overline inline-flex items-center gap-1 text-terra opacity-0 group-hover:opacity-100 transition-opacity">
                      Book this session <ArrowUpRight size={11} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {lbIdx >= 0 && (
        <Lightbox
          images={photos}
          index={lbIdx}
          onIndex={setLbIdx}
          onClose={() => setLbIdx(-1)}
        />
      )}
    </div>
  );
}
